import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { Plus, Send, CheckCircle2, Trash2, Filter, UserCircle, MessageSquare, Phone, Mail } from "lucide-react";

const CATEGORIES = ["complaint", "technical", "billing", "delivery", "feedback", "other"];
const PRIORITIES = ["low", "medium", "high", "urgent"];
const STATUSES = ["open", "in_progress", "resolved", "closed"];

const CAT_META = {
  complaint: { label: "Complaint", cls: "bg-red-100 text-red-700" },
  technical: { label: "Technical", cls: "bg-blue-100 text-blue-700" },
  billing:   { label: "Billing", cls: "bg-amber-100 text-amber-700" },
  delivery:  { label: "Delivery", cls: "bg-purple-100 text-purple-700" },
  feedback:  { label: "Feedback", cls: "bg-emerald-100 text-emerald-700" },
  other:     { label: "Other", cls: "bg-slate-100 text-slate-700" },
};

const PRI_META = {
  low:    "bg-slate-100 text-slate-700",
  medium: "bg-sky-100 text-sky-700",
  high:   "bg-orange-100 text-orange-700",
  urgent: "bg-red-100 text-red-700",
};

const STATUS_META = {
  open:         { label: "Open",        cls: "bg-orange-100 text-orange-700" },
  in_progress:  { label: "In progress", cls: "bg-blue-100 text-blue-700" },
  resolved:     { label: "Resolved",    cls: "bg-emerald-100 text-emerald-700" },
  closed:       { label: "Closed",      cls: "bg-slate-200 text-slate-700" },
};

export default function Support() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isStaff = ["admin", "sales", "accounts", "manager"].includes(user?.role);
  const canDelete = ["admin", "accounts", "manager"].includes(user?.role);

  const [filters, setFilters] = useState({ status: "all", category: "all", priority: "all" });
  const params = Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v && v !== "all"),
  );

  const { data } = useQuery({
    queryKey: ["support", params],
    queryFn: () => api.get("/support", { params }).then((r) => r.data),
    refetchInterval: 30000,
  });
  const { data: summary } = useQuery({
    queryKey: ["support-summary"],
    queryFn: () => api.get("/support/summary").then((r) => r.data),
    enabled: isStaff,
  });
  const { data: staffUsers } = useQuery({
    queryKey: ["support-staff"],
    queryFn: () => api.get("/users").then((r) => r.data),
    enabled: isStaff,
  });
  const staffList = (staffUsers?.items || []).filter((u) =>
    ["admin", "sales", "accounts", "manager", "dispatcher", "operator"].includes(u.role),
  );

  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState({ subject: "", message: "", category: "complaint", priority: "medium" });
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  const create = useMutation({
    mutationFn: (p) => api.post("/support", p),
    onSuccess: (r) => {
      toast.success(`Ticket ${r.data.code} raised`);
      setOpenCreate(false);
      setForm({ subject: "", message: "", category: "complaint", priority: "medium" });
      qc.invalidateQueries({ queryKey: ["support"] });
      qc.invalidateQueries({ queryKey: ["support-summary"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed"),
  });
  const doReply = useMutation({
    mutationFn: ({ id, message }) => api.post(`/support/${id}/reply`, { message }),
    onSuccess: (r) => {
      setActive(r.data);
      setReply("");
      qc.invalidateQueries({ queryKey: ["support"] });
    },
  });
  const patch = useMutation({
    mutationFn: ({ id, patch }) => api.patch(`/support/${id}`, patch),
    onSuccess: (r) => {
      setActive(r.data);
      qc.invalidateQueries({ queryKey: ["support"] });
      qc.invalidateQueries({ queryKey: ["support-summary"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Update failed"),
  });
  const close = useMutation({
    mutationFn: (id) => api.post(`/support/${id}/close`),
    onSuccess: (r) => {
      toast.success("Ticket closed");
      setActive(r.data);
      qc.invalidateQueries({ queryKey: ["support"] });
      qc.invalidateQueries({ queryKey: ["support-summary"] });
    },
  });
  const remove = useMutation({
    mutationFn: (id) => api.delete(`/support/${id}`),
    onSuccess: () => {
      toast.success("Ticket deleted");
      setConfirmDelete(null);
      setActive(null);
      qc.invalidateQueries({ queryKey: ["support"] });
      qc.invalidateQueries({ queryKey: ["support-summary"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Delete failed"),
  });

  return (
    <div className="space-y-4" data-testid="support-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Complaints &amp; Support</h1>
          <p className="text-sm text-muted-foreground">
            {data?.items?.length || 0} of {data?.total || 0} tickets shown
            {isStaff && summary?.by_status && (
              <> · <b className="text-orange-600">{summary.by_status.open || 0} open</b>, <b className="text-blue-600">{summary.by_status.in_progress || 0} in progress</b></>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isStaff && (
            <>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Filter size={12} /></div>
              <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
                <SelectTrigger className="h-9 w-32" data-testid="tkt-filter-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{CAT_META[c].label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                <SelectTrigger className="h-9 w-32" data-testid="tkt-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.priority} onValueChange={(v) => setFilters({ ...filters, priority: v })}>
                <SelectTrigger className="h-9 w-32" data-testid="tkt-filter-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All priorities</SelectItem>
                  {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </>
          )}
          <Dialog open={openCreate} onOpenChange={setOpenCreate}>
            <DialogTrigger asChild>
              <Button data-testid="btn-new-ticket">
                <Plus size={14} className="mr-1" /> Raise ticket
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Raise a support ticket</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="tkt-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{CAT_META[c].label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Subject</Label>
                  <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="tkt-subject" />
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger data-testid="tkt-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Describe the issue</Label>
                  <Textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="tkt-message" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => create.mutate(form)} disabled={!form.subject || !form.message} data-testid="btn-submit-ticket">
                  Submit
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader><CardTitle className="flex items-center gap-2"><MessageSquare size={16} /> Tickets</CardTitle></CardHeader>
          <CardContent className="max-h-[75vh] space-y-2 overflow-y-auto">
            {(data?.items || []).map((t) => {
              const cat = CAT_META[t.category || "other"];
              const st = STATUS_META[t.status || "open"];
              return (
                <button
                  key={t.id}
                  onClick={() => setActive(t)}
                  className={`w-full rounded-lg border p-3 text-left transition-colors ${active?.id === t.id ? "border-primary bg-primary/5" : "border-border/60 hover:border-primary/40"}`}
                  data-testid={`ticket-${t.code}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-mono text-sm font-bold">{t.code}</div>
                    <div className="flex gap-1">
                      <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${cat.cls}`}>{cat.label}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${st.cls}`}>{st.label}</span>
                    </div>
                  </div>
                  <div className="mt-1.5 text-sm font-semibold">{t.subject}</div>
                  <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
                    <span>{t.created_by_name} · {new Date(t.created_at).toLocaleDateString()}</span>
                    <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase ${PRI_META[t.priority || "medium"]}`}>{t.priority}</span>
                  </div>
                  {t.assigned_to_name && (
                    <div className="mt-1 flex items-center gap-1 text-[10px] text-muted-foreground">
                      <UserCircle size={10} /> Assigned to {t.assigned_to_name}
                    </div>
                  )}
                </button>
              );
            })}
            {(!data?.items || data.items.length === 0) && (
              <div className="py-6 text-center text-sm text-muted-foreground">No tickets match.</div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-start justify-between gap-3">
            <div className="flex-1">
              <CardTitle className="mb-2">{active ? `${active.code} · ${active.subject}` : "Select a ticket"}</CardTitle>
              {active && (
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${CAT_META[active.category || "other"].cls}`}>
                    {CAT_META[active.category || "other"].label}
                  </span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_META[active.status || "open"].cls}`}>
                    {STATUS_META[active.status || "open"].label}
                  </span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${PRI_META[active.priority || "medium"]}`}>{active.priority}</span>
                  <span className="text-muted-foreground">From: <b>{active.created_by_name}</b></span>
                </div>
              )}
            </div>
            {active && isStaff && (
              <div className="flex flex-col items-end gap-2">
                {/* Staff controls */}
                <div className="flex flex-wrap items-center justify-end gap-1.5">
                  {active.status !== "closed" && (
                    <Select
                      value={active.status || "open"}
                      onValueChange={(v) => patch.mutate({ id: active.id, patch: { status: v } })}
                    >
                      <SelectTrigger className="h-8 w-32 text-xs" data-testid="tkt-set-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  {active.status !== "closed" && (
                    <Button size="sm" variant="outline" onClick={() => close.mutate(active.id)} data-testid="btn-close-ticket">
                      <CheckCircle2 size={12} className="mr-1" /> Close
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-200 text-red-600 hover:bg-red-50"
                      onClick={() => setConfirmDelete(active)}
                      data-testid="btn-delete-ticket"
                    >
                      <Trash2 size={12} />
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-1.5 text-xs">
                  <span className="text-muted-foreground">Assign:</span>
                  <Select
                    value={active.assigned_to || "unassigned"}
                    onValueChange={(v) => patch.mutate({ id: active.id, patch: { assigned_to: v === "unassigned" ? "" : v } })}
                  >
                    <SelectTrigger className="h-8 w-40 text-xs" data-testid="tkt-assign"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {staffList.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {!active && <div className="py-16 text-center text-sm text-muted-foreground">Pick a ticket from the left.</div>}
            {active && (
              <div className="space-y-3">
                <div className="max-h-96 space-y-2 overflow-y-auto rounded border border-border/40 p-3">
                  {(active.replies || []).map((r) => (
                    <div
                      key={`${r.at}-${r.by}`}
                      className={`rounded-lg p-3 ${r.role === "customer" ? "border-l-4 border-primary bg-primary/5" : "border-l-4 border-emerald-500 bg-emerald-50"}`}
                    >
                      <div className="flex items-center justify-between text-xs">
                        <b>{r.by} <span className="ml-1 rounded bg-slate-200 px-1 py-0.5 text-[9px] uppercase text-slate-700">{r.role}</span></b>
                        <span className="text-muted-foreground">{new Date(r.at).toLocaleString()}</span>
                      </div>
                      <div className="mt-1 whitespace-pre-wrap text-sm">{r.message}</div>
                    </div>
                  ))}
                </div>
                {active.status !== "closed" && (
                  <div className="space-y-2">
                    <Textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type your reply…" data-testid="ticket-reply" />
                    <Button onClick={() => doReply.mutate({ id: active.id, message: reply })} disabled={!reply.trim()} data-testid="btn-send-reply">
                      <Send size={12} className="mr-1" /> Send reply
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete ticket?</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{confirmDelete?.code} · {confirmDelete?.subject}</b> will be permanently removed along with its reply thread.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDelete && remove.mutate(confirmDelete.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="tkt-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Contact FuelExpress support (rendered under the tickets list) */}
      <ContactCard />
    </div>
  );
}


/* ─────────────────── Contact FuelExpress card (CMS-driven) ─────────────────── */
function ContactCard() {
  const { data } = useQuery({
    queryKey: ["cms-landing"],
    queryFn: () => api.get("/cms/landing").then((r) => r.data),
    staleTime: 60_000,
  });
  const c = data?.content?.support_contact || data?.support_contact || {};
  const phone = c.phone || "";
  const whatsapp = c.whatsapp || "";
  const email = c.email || "";
  const hours = c.hours || "";

  // Nothing configured → hide the card entirely (rather than show broken links)
  if (!phone && !whatsapp && !email) return null;

  const digits = (v) => (v || "").replace(/[^0-9+]/g, "");

  return (
    <Card data-testid="support-contact-card">
      <CardContent className="p-5">
        <div className="mb-3 flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-primary" />
          <h2 className="text-sm font-bold uppercase tracking-widest text-primary">
            Talk to FuelExpress
          </h2>
        </div>
        <p className="mb-4 text-sm text-muted-foreground">
          Prefer to skip the ticket queue? Reach our support team directly on any of these channels:
          {hours && <span className="ml-1 font-medium text-foreground">· {hours}</span>}
        </p>
        <div className="grid gap-2 sm:grid-cols-3">
          {phone && (
            <a
              href={`tel:${digits(phone)}`}
              className="flex items-center gap-3 rounded-lg border p-3 transition-all hover:border-primary/40 hover:bg-primary/[0.03]"
              data-testid="support-contact-phone"
            >
              <div className="rounded-md bg-primary/10 p-2 text-primary">
                <Phone className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Call</div>
                <div className="truncate text-sm font-semibold">{phone}</div>
              </div>
            </a>
          )}
          {whatsapp && (
            <a
              href={`https://wa.me/${digits(whatsapp).replace(/^\+/, "")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-lg border p-3 transition-all hover:border-green-500/40 hover:bg-green-500/[0.04]"
              data-testid="support-contact-whatsapp"
            >
              <div className="rounded-md bg-green-500/10 p-2 text-green-600">
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.966-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.05 21.785h-.004a9.87 9.87 0 01-5.031-1.378l-.36-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884zm8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </div>
              <div className="min-w-0">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">WhatsApp</div>
                <div className="truncate text-sm font-semibold">{whatsapp}</div>
              </div>
            </a>
          )}
          {email && (
            <a
              href={`mailto:${email}`}
              className="flex items-center gap-3 rounded-lg border p-3 transition-all hover:border-primary/40 hover:bg-primary/[0.03]"
              data-testid="support-contact-email"
            >
              <div className="rounded-md bg-primary/10 p-2 text-primary">
                <Mail className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Email</div>
                <div className="truncate text-sm font-semibold">{email}</div>
              </div>
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
